import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/switch.default.js';
import * as wireframeSkin from './skins/switch.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunSwitch extends RunElement {
  #checked = false;
  #disabled = false;
  #required = false;
  #value = 'on';
  #name = '';
  #rendering = false;
  #internals = null;

  static formAssociated = true;

  static get observedAttributes() {
    return ['checked', 'disabled', 'required', 'value', 'name', 'skin'];
  }

  _onClickBound = (e) => this.#handleClick(e);
  _onKeydownBound = (e) => this.#handleKeydown(e);
  _onFocusBound = () => this.setAttribute('data-focus-visible', '');
  _onBlurBound = () => this.removeAttribute('data-focus-visible');

  constructor() {
    super();
    this.#internals = this.attachInternals();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'switch');
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('keydown', this._onKeydownBound);
    this.addEventListener('focus', this._onFocusBound);
    this.addEventListener('blur', this._onBlurBound);
    this.#updateFormState();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('keydown', this._onKeydownBound);
    this.removeEventListener('focus', this._onFocusBound);
    this.removeEventListener('blur', this._onBlurBound);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'checked':  this.checked  = newValue !== null; break;
      case 'disabled': this.disabled = newValue !== null; break;
      case 'required': this.required = newValue !== null; break;
      case 'value':    this.value    = newValue !== null ? newValue : 'on'; break;
      case 'name':     this.name     = newValue !== null ? newValue : ''; break;
      case 'skin':     this.requestUpdate(); break;
    }
  }

  formResetCallback() {
    this.checked  = this.hasAttribute('checked');
    this.disabled = false;
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state, mode) {
    if (mode === 'restore' && typeof state === 'string') {
      this.checked = state === this.#value;
    }
  }

  get checked() { return this.#checked; }
  set checked(value) {
    const coerced = Boolean(value);
    if (this.#checked === coerced) return;
    this.#checked = coerced;
    this.toggleAttribute('checked', coerced);
    this.#updateFormState();
    this.#updateAria();
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(value) {
    const coerced = Boolean(value);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get required() { return this.#required; }
  set required(value) {
    const coerced = Boolean(value);
    if (this.#required === coerced) return;
    this.#required = coerced;
    this.toggleAttribute('required', coerced);
    this.#updateFormState();
    this.#updateAria();
    this.requestUpdate();
  }

  get value() { return this.#value; }
  set value(value) {
    const coerced = String(value).trim();
    if (this.#value === coerced) return;
    this.#value = coerced;
    this.setAttribute('value', coerced);
    this.#updateFormState();
    this.requestUpdate();
  }

  get name() { return this.#name; }
  set name(value) {
    const coerced = String(value).trim();
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) { this.setAttribute('name', coerced); } else { this.removeAttribute('name'); }
    this.requestUpdate();
  }

  get skin() { return this.getAttribute('skin'); }

  checkValidity()    { return this.#internals.checkValidity(); }
  reportValidity()   { return this.#internals.reportValidity(); }
  setCustomValidity(msg) { this.#internals.setValidity(msg ? { customError: true } : {}, msg || ''); }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #resolveSkin() {
    const instanceSkin = this.getAttribute('skin');
    if (instanceSkin) return instanceSkin;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-checked', String(this.#checked));
    if (this.#disabled) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
    if (this.#required) {
      this.setAttribute('aria-required', 'true');
    } else {
      this.removeAttribute('aria-required');
    }
    this.setAttribute('tabindex', this.#disabled ? '-1' : '0');
  }

  #updateFormState() {
    if (!this.#internals) return;
    if (this.#checked) {
      this.#internals.setFormValue(this.#value, this.#value);
    } else {
      this.#internals.setFormValue(null);
    }
    if (this.#required && !this.#checked) {
      this.#internals.setValidity(
        { valueMissing: true },
        'This field is required.',
        this.shadowRoot ? this.shadowRoot.firstElementChild : undefined,
      );
    } else {
      this.#internals.setValidity({});
    }
  }

  #handleClick() {
    if (this.#disabled) return;
    this.#toggle();
  }

  #handleKeydown(event) {
    if (this.#disabled) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#toggle();
    }
  }

  #toggle() {
    this.checked = !this.#checked;
    this.#emitChange();
  }

  #emitChange() {
    const detail = { checked: this.#checked, value: this.#checked ? this.#value : null };
    this.dispatchEvent(new CustomEvent('run-change', { bubbles: true, composed: true, detail }));
    this.dispatchEvent(new CustomEvent('change',     { bubbles: true, composed: true, detail }));
  }
}

if (!customElements.get('run-switch')) {
  customElements.define('run-switch', RunSwitch);
}

export { RunSwitch };
export default RunSwitch;
